import {
  Box,
  Heading,
  SimpleGrid,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { date as commonDate } from '@finley-technologies/common';

import {
  FundingVerificationPipelineRunGQLType,
  useGetFundingVerificationPipelineRunsQuery,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { useGetLenderVerificationCreditFacilitiesQuery } from '../requests/requests.gql.generated';
import { StatusBadge } from './StatusBadge';
import { useVerificationOptions } from './VerificationTableSharedComponents';
import { colors } from 'packages/common-frontend/colors';
import {
  BreadcrumbProps,
  Breadcrumbs,
} from 'packages/common-frontend/components/Breadcrumbs';
import { FullDateRangePicker } from 'packages/common-frontend/components/DateRangePicker';
import { IsolatedMultiSelect } from 'packages/common-frontend/components/MultiSelect';
import {
  PaginationControl,
  usePaginationLimit,
} from 'packages/common-frontend/components/PaginationControl';
import { useGetCreditFacilityQuery } from 'packages/credit-facility/requests/requests.gql.generated';

type HistoryTableRowProps = {
  pipelineRun: FundingVerificationPipelineRunGQLType;
};
const HistoryTableRow = ({
  pipelineRun: {
    artifactDocumentId,
    creditFacilityId,
    reportingDate,
    fileVersion,
    status,
  },
}: HistoryTableRowProps) => {
  const navigate = useNavigate();
  const { navigator } = useVerificationOptions();

  // TODO(jon): include credit facility info in the payload so we don't need a separate query.
  const { data: creditFacilityData } = useGetCreditFacilityQuery({
    variables: { creditFacilityId },
  });
  if (!creditFacilityData) return null;

  return (
    <Tr
      _hover={{ bg: 'gray.100' }}
      cursor="pointer"
      onClick={() =>
        navigator(navigate).setPipeline({
          artifactDocumentId,
          creditFacilityId,
          reportingDate,
          fileVersion,
        })
      }
    >
      <Td>{commonDate.fullDateStrToHumanDateString(reportingDate)}</Td>
      <Td>{creditFacilityData.creditFacility.name}</Td>
      <Td textAlign="center" width="150px">
        <StatusBadge value={status} />
      </Td>
    </Tr>
  );
};

type HistoryTableProps = {
  pipelineRuns: FundingVerificationPipelineRunGQLType[];
};
const HistoryTable = ({ pipelineRuns }: HistoryTableProps) => {
  return (
    <TableContainer whiteSpace="normal">
      <Table variant="simple">
        <Thead
          background={colors.tableHeader}
          borderBottom="1px solid lightgrey"
          padding="8px 0"
        >
          <Tr>
            <Th>Verification Report Date</Th>
            <Th>Credit Facility</Th>
            <Th textAlign="center">Status</Th>
          </Tr>
        </Thead>
        <Tbody>
          {pipelineRuns.map((pipelineRun) => (
            <HistoryTableRow
              pipelineRun={pipelineRun}
              key={`${pipelineRun.artifactDocumentId}-${pipelineRun.creditFacilityId}-${pipelineRun.reportingDate}-${pipelineRun.fileVersion}`}
            />
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

type VerificationHistoryTableProps = {
  organizationId: string;
};
export const VerificationHistoryTable = ({
  organizationId,
}: VerificationHistoryTableProps) => {
  // Filters
  const [creditFacilityIds, setCreditFacilityIds] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  // Pagination
  const [startIndex, setStartIndex] = useState<number>(0);
  const { pageLimit, setPageLimit, options } = usePaginationLimit(10, [
    10,
    25,
    50,
    100,
  ]);

  // Reset start index when changing filter criteria, since that may put the current stat index
  // past the end of the list.
  useEffect(() => setStartIndex(0), [creditFacilityIds, startDate, endDate]);

  const onPrevious = useCallback(
    () => setStartIndex(Math.max(0, startIndex - pageLimit)),
    [startIndex, pageLimit],
  );
  const onNext = useCallback(() => setStartIndex(startIndex + pageLimit), [
    startIndex,
    pageLimit,
  ]);

  const {
    data: creditFacilitiesData,
  } = useGetLenderVerificationCreditFacilitiesQuery({
    variables: { organizationId },
    onCompleted: (data) =>
      setCreditFacilityIds(
        data?.lenderVerificationCreditFacilities.map(({ id }) => id) ?? [],
      ),
  });
  const creditFacilityOptions = useMemo(
    () =>
      creditFacilitiesData?.lenderVerificationCreditFacilities.map(
        (facility) => ({
          value: facility.id,
          label: facility.name,
        }),
      ) ?? [],
    [creditFacilitiesData],
  );

  const { data: pipelineRunsData } = useGetFundingVerificationPipelineRunsQuery(
    {
      fetchPolicy: 'network-only',
      variables: { organizationId },
    },
  );
  const pipelineRuns = pipelineRunsData?.fundingVerificationPipelineRuns;

  // TODO(jon): do filtering and/or pagination in the backend.
  const { filteredPipelineRuns, hasPreviousPage, hasNextPage } = useMemo(() => {
    let filteredRuns =
      pipelineRuns?.filter(({ creditFacilityId, reportingDate }) => {
        const jsReportingDate = commonDate.fullDateStrToDate(reportingDate);
        if (startDate && jsReportingDate < startDate) return false;
        if (endDate && jsReportingDate > endDate) return false;
        if (!creditFacilityIds.includes(creditFacilityId)) return false;
        return true;
      }) ?? [];

    const filteredRunsMap = new Map<
      string,
      FundingVerificationPipelineRunGQLType
    >();
    filteredRuns.forEach((run) => {
      const key = `${run.reportingDate}-${run.creditFacilityId}`;
      const previousRun = filteredRunsMap.get(key);
      if (!previousRun || previousRun.fileVersion < run.fileVersion) {
        filteredRunsMap.set(key, run);
      }
    });
    filteredRuns = Array.from(filteredRunsMap.values());

    return {
      filteredPipelineRuns: filteredRuns.slice(
        startIndex,
        startIndex + pageLimit,
      ),
      hasPreviousPage: startIndex > 0,
      hasNextPage: startIndex + pageLimit < filteredRuns.length,
    };
  }, [
    pipelineRuns,
    startDate,
    endDate,
    creditFacilityIds,
    startIndex,
    pageLimit,
  ]);

  if (!pipelineRuns) {
    return (
      <Skeleton height="600px" data-testid="funding-request-history-loading" />
    );
  }

  const breadcrumbs: BreadcrumbProps[] = [
    {
      label: 'Funding Request',
      path: '/funding-requests/',
    },
    {
      label: 'Verification Reports',
    },
  ];

  return (
    <Box height="100%" width="100%">
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <Heading as="h2" mt="16px" mb="32px">
        All Verification Reports
      </Heading>
      <PaginationControl
        filters={
          <SimpleGrid
            columns={{
              sm: 1,
              md: 2,
              lg: 2,
              xl: 4,
            }}
            spacingX="1rem"
            spacingY="1rem"
          >
            <IsolatedMultiSelect
              applyButtonText="Apply filters"
              buttonProps={{ width: '100%' }}
              initialSelections={creditFacilityIds}
              isSearchable
              menuListProps={{ minWidth: '30rem' }}
              onApplyOptions={setCreditFacilityIds}
              options={creditFacilityOptions}
              optionsHeader="Credit facilities"
              placeholderText="Select credit facilities"
              showSelectedCount
              titleText="All credit facilities"
            />
            <FullDateRangePicker
              currentDateRange={{ startDate, endDate }}
              onSelectDateRange={(newDateRange) => {
                const [newStartDate, newEndDate] = newDateRange;
                if (newStartDate && newEndDate) {
                  setStartDate(newStartDate);
                  setEndDate(newEndDate);
                }
              }}
              width="xs"
            />
          </SimpleGrid>
        }
        pageLimit={pageLimit}
        setPageLimit={setPageLimit}
        options={options}
        limitLabel="Items Per Page"
        previousLabel="Newer"
        nextLabel="Older"
        onPrevious={onPrevious}
        onNext={onNext}
        hasPreviousPage={hasPreviousPage}
        hasNextPage={hasNextPage}
      >
        <>
          <Box height="20px" />
          <HistoryTable pipelineRuns={filteredPipelineRuns} />
        </>
      </PaginationControl>
    </Box>
  );
};
